import React, { useState, useMemo, useContext } from "react";
import { products, calculatePrice } from "../../data/pricing";
import { LanguageContext } from "../../context/LanguageContext";
import no from "../../context/no";
import en from "../../context/en";

// Format price with Norwegian locale
const formatPrice = (price) =>
  new Intl.NumberFormat("nb-NO").format(price) + " NOK";

// Iris counter component
const IrisCounter = ({ value, onChange, min = 1, max = 5, t }) => (
  <div className="flex items-center gap-4">
    <label className="text-main-dark font-copy">{t.irisCount}</label>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-8 h-8 flex items-center justify-center bg-secondary-dark text-main-light rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-main-dark transition-colors"
      >
        -
      </button>
      <span className="w-8 text-center text-main-dark font-bold">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-8 h-8 flex items-center justify-center bg-secondary-dark text-main-light rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-main-dark transition-colors"
      >
        +
      </button>
    </div>
  </div>
);

// Size selector component
const SizeSelector = ({ sizes, value, onChange, label, t }) => (
  <div className="flex flex-col gap-2">
    <label className="text-main-dark font-copy">{label || t.size}:</label>
    <div className="flex flex-wrap gap-2">
      {sizes.map((size) => (
        <button
          key={size.id}
          type="button"
          onClick={() => onChange(size.id)}
          className={`px-3 py-2 text-sm font-copy border transition-all ${
            value === size.id
              ? "bg-secondary-dark text-main-light border-secondary-dark"
              : "bg-main-light text-main-dark border-main-dark hover:bg-grey/30"
          }`}
        >
          {size.label}
        </button>
      ))}
    </div>
  </div>
);

// Shape selector for products with multiple shapes
const ShapeSelector = ({ shapes, value, onChange, t }) => (
  <div className="flex flex-col gap-2">
    <label className="text-main-dark font-copy">{t.shape}</label>
    <div className="flex gap-2">
      {shapes.map((shape) => (
        <button
          key={shape.id}
          type="button"
          onClick={() => onChange(shape.id)}
          className={`px-4 py-2 text-sm font-copy border transition-all ${
            value === shape.id
              ? "bg-secondary-dark text-main-light border-secondary-dark"
              : "bg-main-light text-main-dark border-main-dark hover:bg-grey/30"
          }`}
        >
          {shape.label}
        </button>
      ))}
    </div>
    {shapes.find((s) => s.id === value)?.note && (
      <p className="text-sm text-main-dark/50 italic">
        {shapes.find((s) => s.id === value).note}
      </p>
    )}
  </div>
);

// Frame toggle for paper products
const FrameToggle = ({ checked, onChange, framePrice, t }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-5 h-5 accent-iris-brown cursor-pointer"
    />
    <span className="text-main-dark font-copy">
      {t.includeFrame}
      {framePrice != null ? ` (+${formatPrice(framePrice)})` : ""}
    </span>
  </label>
);

// Frame type selector (e.g. acrylic vs glass)
const FrameTypeSelector = ({ frames, value, onChange, t }) => (
  <div className="flex flex-col gap-2 pl-8">
    <label className="text-main-dark font-copy">{t.frameType}</label>
    <div className="flex flex-wrap gap-2">
      {frames.map((frame) => (
        <button
          key={frame.id}
          type="button"
          onClick={() => onChange(frame.id)}
          className={`px-3 py-2 text-sm font-copy border transition-all ${
            value === frame.id
              ? "bg-secondary-dark text-main-light border-secondary-dark"
              : "bg-main-light text-main-dark border-main-dark hover:bg-grey/30"
          }`}
        >
          {frame.label} (+{formatPrice(frame.price)})
        </button>
      ))}
    </div>
  </div>
);

// Price breakdown display
const PriceBreakdown = ({ items, total, t }) => (
  <div className="mt-6 pt-4 border-t border-grey/50">
    <div className="space-y-1 mb-3">
      {items.map((item, i) => (
        <div key={i} className="flex justify-between">
          <span className="text-main-dark/70">{item.label}</span>
          <span className="text-main-dark/70">{formatPrice(item.price)}</span>
        </div>
      ))}
    </div>
    <div className="flex justify-between items-center pt-3 border-t border-main-dark">
      <span className="text-main-dark font-bold text-lg">{t.total}</span>
      <span className="text-iris-brown font-bold text-xl">
        {formatPrice(total)}
      </span>
    </div>
  </div>
);

// Digital product calculator
const DigitalCalculator = ({ product, t, lang }) => {
  const [irisCount, setIrisCount] = useState(1);
  const price = product.irisPricing[irisCount];
  const digitalLabel = lang === "no" ? "Digital fil" : "Digital file";

  return (
    <div className="space-y-4">
      <IrisCounter
        value={irisCount}
        onChange={setIrisCount}
        max={product.maxIris}
        t={t}
      />
      <PriceBreakdown
        items={[{ label: `${digitalLabel} (${irisCount} iris)`, price }]}
        total={price}
        t={t}
      />
    </div>
  );
};

// Paper product calculator
const PaperCalculator = ({ product, t, lang }) => {
  const [sizeId, setSizeId] = useState(product.sizes[0].id);
  const [irisCount, setIrisCount] = useState(1);
  const [includeFrame, setIncludeFrame] = useState(true);

  const selectedSize = product.sizes.find((s) => s.id === sizeId);
  const frameOption = product.frames.find((f) => f.id === sizeId);

  const paperLabel = lang === "no" ? "Fotopapir" : "Photo paper";
  const extraIrisLabel = lang === "no" ? "Ekstra iris" : "Extra iris";
  const frameLabel = lang === "no" ? "Ramme" : "Frame";

  const breakdown = useMemo(() => {
    const items = [{ label: `${paperLabel} ${selectedSize.label}`, price: selectedSize.price }];
    if (irisCount > 1) {
      items.push({
        label: `${extraIrisLabel} (${irisCount - 1} stk)`,
        price: (irisCount - 1) * product.additionalIrisPrice,
      });
    }
    if (includeFrame && frameOption) {
      items.push({ label: frameLabel, price: frameOption.price });
    }
    return items;
  }, [selectedSize, irisCount, includeFrame, frameOption, product.additionalIrisPrice, paperLabel, extraIrisLabel, frameLabel]);

  const total = calculatePrice(product.id, { sizeId, irisCount, includeFrame });

  return (
    <div className="space-y-4">
      <SizeSelector
        sizes={product.sizes}
        value={sizeId}
        onChange={setSizeId}
        t={t}
      />
      <IrisCounter
        value={irisCount}
        onChange={setIrisCount}
        max={product.maxIris}
        t={t}
      />
      {frameOption && (
        <FrameToggle
          checked={includeFrame}
          onChange={setIncludeFrame}
          framePrice={frameOption.price}
          t={t}
        />
      )}
      <PriceBreakdown items={breakdown} total={total} t={t} />
    </div>
  );
};

// A2 print calculator — fixed size, optional acrylic/glass frame
const A2Calculator = ({ product, t, lang }) => {
  const [irisCount, setIrisCount] = useState(1);
  const [includeFrame, setIncludeFrame] = useState(true);
  const [frameId, setFrameId] = useState(product.frames[0].id);

  const selectedFrame = product.frames.find((f) => f.id === frameId);
  const printLabel = lang === "no" ? "A2 Print" : "A2 Print";
  const extraIrisLabel = lang === "no" ? "Ekstra iris" : "Extra iris";

  const breakdown = useMemo(() => {
    const items = [{ label: printLabel, price: product.price }];
    if (irisCount > 1) {
      items.push({
        label: `${extraIrisLabel} (${irisCount - 1} stk)`,
        price: (irisCount - 1) * product.additionalIrisPrice,
      });
    }
    if (includeFrame && selectedFrame) {
      items.push({ label: selectedFrame.label, price: selectedFrame.price });
    }
    return items;
  }, [
    irisCount,
    includeFrame,
    selectedFrame,
    product.price,
    product.additionalIrisPrice,
    printLabel,
    extraIrisLabel,
  ]);

  const total = calculatePrice(product.id, {
    irisCount,
    includeFrame,
    frameId,
  });

  return (
    <div className="space-y-4">
      <IrisCounter
        value={irisCount}
        onChange={setIrisCount}
        max={product.maxIris}
        t={t}
      />
      <FrameToggle
        checked={includeFrame}
        onChange={setIncludeFrame}
        t={t}
      />
      {includeFrame && (
        <FrameTypeSelector
          frames={product.frames}
          value={frameId}
          onChange={setFrameId}
          t={t}
        />
      )}
      <PriceBreakdown items={breakdown} total={total} t={t} />
    </div>
  );
};

// Size with iris tiers calculator (Acrylic/Alu Square, Circle)
const SizeWithTiersCalculator = ({ product, t }) => {
  const [shapeId, setShapeId] = useState(product.shapes[0].id);
  const [sizeId, setSizeId] = useState(product.shapes[0].sizes[0].id);
  const [irisCount, setIrisCount] = useState(1);

  const selectedShape = product.shapes.find((s) => s.id === shapeId);
  const selectedSize = selectedShape?.sizes.find((s) => s.id === sizeId);

  // Reset size when shape changes
  const handleShapeChange = (newShapeId) => {
    setShapeId(newShapeId);
    const newShape = product.shapes.find((s) => s.id === newShapeId);
    if (newShape) {
      setSizeId(newShape.sizes[0].id);
    }
  };

  const breakdown = useMemo(() => {
    if (!selectedSize) return [];
    const items = [{ label: `${selectedShape.label} ${selectedSize.label}`, price: selectedSize.price }];
    if (irisCount > 1 && product.irisTiers[irisCount]) {
      items.push({
        label: `${irisCount} iris`,
        price: product.irisTiers[irisCount],
      });
    }
    return items;
  }, [selectedShape, selectedSize, irisCount, product.irisTiers]);

  const total = calculatePrice(product.id, { shapeId, sizeId, irisCount });

  return (
    <div className="space-y-4">
      {product.shapes.length > 1 && (
        <ShapeSelector
          shapes={product.shapes}
          value={shapeId}
          onChange={handleShapeChange}
          t={t}
        />
      )}
      {selectedShape && (
        <SizeSelector
          sizes={selectedShape.sizes}
          value={sizeId}
          onChange={setSizeId}
          t={t}
        />
      )}
      <IrisCounter
        value={irisCount}
        onChange={setIrisCount}
        max={product.maxIris}
        t={t}
      />
      <PriceBreakdown items={breakdown} total={total} t={t} />
    </div>
  );
};

// Line matrix calculator (Acrylic/Alu Line)
const LineMatrixCalculator = ({ product, t }) => {
  const [irisCount, setIrisCount] = useState(product.minIris || 2);
  const [sizeId, setSizeId] = useState(product.matrix[product.minIris || 2][0].id);

  const availableSizes = product.matrix[irisCount] || [];
  const selectedSize = availableSizes.find((s) => s.id === sizeId);

  // Reset size when iris count changes
  const handleIrisChange = (newCount) => {
    setIrisCount(newCount);
    const newSizes = product.matrix[newCount];
    if (newSizes && newSizes.length > 0) {
      setSizeId(newSizes[0].id);
    }
  };

  const total = selectedSize?.price || 0;

  return (
    <div className="space-y-4">
      <IrisCounter
        value={irisCount}
        onChange={handleIrisChange}
        min={product.minIris || 2}
        max={product.maxIris}
        t={t}
      />
      <SizeSelector
        sizes={availableSizes}
        value={sizeId}
        onChange={setSizeId}
        label={`${t.size} (${irisCount} iris)`}
        t={t}
      />
      <PriceBreakdown
        items={[{ label: `Line ${selectedSize?.label || ""} (${irisCount} iris)`, price: total }]}
        total={total}
        t={t}
      />
    </div>
  );
};

// Main PriceCalculator component
const PriceCalculator = ({ productId }) => {
  const { language } = useContext(LanguageContext);
  const t = language === "en" ? en.priceCalculator : no.priceCalculator;
  const product = products[productId];
  const unknownProductType = language === "no" ? "Ukjent produkttype" : "Unknown product type";
  const productNotFound = language === "no" ? "Produkt ikke funnet" : "Product not found";

  if (!product) {
    return <div className="text-warning">{productNotFound}</div>;
  }

  const renderCalculator = () => {
    switch (product.type) {
      case "digital":
        return <DigitalCalculator product={product} t={t} lang={language} />;
      case "paper":
        return <PaperCalculator product={product} t={t} lang={language} />;
      case "paperA2":
        return <A2Calculator product={product} t={t} lang={language} />;
      case "sizeWithIrisTiers":
        return <SizeWithTiersCalculator product={product} t={t} />;
      case "lineMatrix":
        return <LineMatrixCalculator product={product} t={t} />;
      default:
        return <div>{unknownProductType}</div>;
    }
  };

  return (
    <div className="bg-main-light p-6 mt-4 border-t border-grey/30">
      <h5 className="text-main-dark font-heading font-bold mb-4">
        {t.title}
      </h5>
      {renderCalculator()}
      <p className="text-sm mt-4 text-main-dark/50">
        {t.contactUs} {product.maxIris} {t.iris}.
      </p>
    </div>
  );
};

export default PriceCalculator;

